﻿import { useState, useCallback, useMemo, useRef } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';

import { PagedRequest } from "../../DTOs/Common/PagedRequest";
import { PagedResult } from "../../DTOs/Common/PagedResult";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { Cancelled } from "../../types/ErrorTypes";
import i18n from "../../i18n";


export interface DataTableFilteredRequest extends PagedRequest { [key: string]: any; }

// Note: clientPaginate asks the server for everything in one page; paging is then done in-memory.
const CLIENT_PAGINATE_FETCH_ALL = 100000;

export interface UseDataTableOptions<T, TableMetaData = undefined, FetchResultType extends PagedResult<T> = PagedResult<T>, ID = number> {
  /** Cache identity key. Include every variable the fetch depends on (filters, date, shift, etc.).
   *  React Query re-fetches automatically whenever this changes. */
  queryKey: readonly unknown[];
  /** Fetches a page of data. Receives the current page/sort/filter params. */
  fetchFn: (request: DataTableFilteredRequest) => Promise<FetchResultType | ErrorResponse>;
  /** Page to start on. Defaults to `1`. */
  initialPage?: number;
  /** Rows per page. Defaults to `10`. */
  pageSize?: number;
  /** Fetch the entire result set once and paginate/sort it in-memory instead of per-page server
   *  requests. Use for bounded, shift-scoped lists: it makes every page instantly available
   *  offline from a single cached response and lets `pageSize` change without a refetch.
   *  `fetchFn` still receives a request, but with a very large `pageSize` so it returns everything. */
  clientPaginate?: boolean;
  /** Set to `false` to suspend fetching (e.g. while a required filter is not yet selected).
   *  Defaults to `true`. */
  enabled?: boolean;
  /** When `true`, the table fetches and displays a single row instead of a page.
   *  Requires `singleEntryId` and `fetchSingleFn`. */
  singleEntryMode?: boolean;
  /** ID of the single entry to fetch when `singleEntryMode` is enabled. */
  singleEntryId?: ID | null;
  /** Fetches a single row by ID. Only used when `singleEntryMode` is `true`. */
  fetchSingleFn?: (id: ID) => Promise<T | ErrorResponse>;
  /** Derives extra metadata from the raw fetch result (e.g. server-side aggregate totals for
   *  a footer). The result is exposed as `tableMetaData` and updates in the same render as
   *  the row data. */
  selectMeta?: (result: FetchResultType) => TableMetaData;
}

export interface UseDataTableResult<T, TableMetaData, FetchResultType extends PagedResult<T>> {
  /** The full React Query cache key for this table's current page/sort. Pass to
   *  `queryClient.setQueryData` or `invalidateQueries` from `useMutation` callbacks. */
  queryKey: readonly unknown[];
  /** Raw server rows for the current page. Translate them into `TableRow`s at the call site. */
  rows: T[];
  /** Every row in the cache, or `undefined` until a fetch succeeds. Differs from `rows` only with
   *  `clientPaginate`. Changes on each fetch and on each row-helper patch, so an effect on it runs
   *  after local edits too. */
  fetchedRows: T[] | undefined;
  /** Data derived from the fetch result via `selectMeta` (e.g. aggregate totals for a footer).
   *  `undefined` when `selectMeta` is not provided or the query has not yet resolved. */
  tableMetaData: TableMetaData | undefined;
  /** `true` during the very first load when no cached data exists yet.
   *  Use to render a full-table spinner. */
  isLoading: boolean;
  /** `true` when silently re-fetching while stale data is still displayed.
   *  Use to show a subtle progress state without replacing the existing rows. */
  isBackgroundFetching: boolean;
  /** Error message if the last fetch failed, otherwise `null`. */
  error: string | null;
  /** Currently displayed page number (1-based). */
  pageNumber: number;
  /** Number of rows per page. */
  pageSize: number;
  /** Total row count across all pages as reported by the server. */
  totalCount: number;
  /** Total page count as reported by the server. */
  totalPages: number;
  /** Active sort column name, or `undefined` if the table is unsorted. */
  sortField: string | undefined;
  /** Current sort direction. */
  sortDirection: 'asc' | 'desc';
  /** Navigate to a specific page number. */
  setPage: (page: number) => void;
  /** Toggle sort on a column. Same column → reverses direction. New column → sorts ascending. */
  handleSortChange: (field: string) => void;
  /** Optimistically replaces a matching row in the cache without refetching.
   *  Pass `metaPatch` to also merge server-supplied aggregates (e.g. `totalLoads`) into the
   *  cached result ; `selectMeta` re-derives `tableMetaData` on the next render. */
  updateRowSource: (
    idSelector: (r: T) => boolean,
    newRow: T,
    metaPatch?: Partial<FetchResultType>,
  ) => void;
  /** Optimistically prepends a new row to the cache and increments `totalCount`.
   *  Pass `metaPatch` to also merge server-supplied aggregates into the cached result.
   *  Patched fields override the helper's defaults (e.g. supply `totalCount` to trust the
   *  server's count over the local +1). */
  appendRowSource: (
    newItem: T,
    metaPatch?: Partial<FetchResultType>,
  ) => void;
  /** Optimistically removes a matching row from the cache and decrements `totalCount`.
   *  Pass `metaPatch` to also merge server-supplied aggregates into the cached result. */
  removeRowSource: (
    idSelector: (r: T) => boolean,
    metaPatch?: Partial<FetchResultType>,
  ) => void;
  /** Low-level escape hatch for cache patches not covered by the helpers above.
   *  Prefer `updateRowSource` / `appendRowSource` / `removeRowSource` where possible. */
  mutateCachedResult: (mutator: (old: FetchResultType) => FetchResultType) => void;
  /** Invalidates all cached pages/sorts for this table, triggering a background refetch.
   *  Prefer this over `refresh` after mutations — it clears stale data across every page,
   *  not just the currently visible one. */
  invalidate: () => void;
  /** Hard-refetches only the currently visible page/sort combination.
   *  Use when you need to stay on the current page after an operation (e.g. registering an
   *  external refresh callback). For most post-mutation cases, prefer `invalidate`. */
  refresh: () => void;
}

export function useDataTable<T, TableMetaData = undefined, FetchResultType extends PagedResult<T> = PagedResult<T>, ID = number>(
  options: UseDataTableOptions<T, TableMetaData, FetchResultType, ID>
): UseDataTableResult<T, TableMetaData, FetchResultType> {
  const {
    queryKey: callerQueryKey,
    fetchFn,
    pageSize = 10,
    clientPaginate = false,
    enabled = true,
    singleEntryMode,
    singleEntryId,
    fetchSingleFn,
    selectMeta,
  } = options;

  const [pageNumber, setPage] = useState(options.initialPage ?? 1);
  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const queryClient = useQueryClient();

  const callerQueryKeyJson = JSON.stringify(callerQueryKey, (_, value) => {
    if (value === undefined) return null;
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      return Object.fromEntries(
        Object.entries(value).sort(([a], [b]) => a.localeCompare(b))
      );
    }
    return value;
  });

  const prevCallerQueryKeyJson = useRef(callerQueryKeyJson);
  if (prevCallerQueryKeyJson.current !== callerQueryKeyJson) {
    prevCallerQueryKeyJson.current = callerQueryKeyJson;
    if (pageNumber !== 1) setPage(1);
  }

  const queryKey = useMemo(() => clientPaginate ? [
    'dataTable',
    ...callerQueryKey,
    'all',
    singleEntryMode ?? false,
    singleEntryId ?? null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ] : [
    'dataTable',
    ...callerQueryKey,
    pageNumber,
    pageSize,
    sortField ?? null,
    sortDirection,
    singleEntryMode ?? false,
    singleEntryId ?? null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [callerQueryKeyJson, clientPaginate, pageNumber, pageSize, sortField, sortDirection, singleEntryMode, singleEntryId]);

  const { data: queryResult, isLoading, isPending, isFetching, error: queryError, refetch } = useQuery<FetchResultType, Error>({
    queryKey,
    queryFn: async () => {
      if (singleEntryMode && singleEntryId != null && fetchSingleFn) {
        const data = await fetchSingleFn(singleEntryId);
        if (ErrorResponse.isErrorResponse(data)) throw new Error(data.messages.join(', '));

        return (data
          ? new PagedResult<T>([data], 1, 1, 1, pageSize)
          : PagedResult.empty<T>(1, pageSize)
        ) as FetchResultType;
      }

      const result = await fetchFn(clientPaginate
        ? { pageNumber: 1, pageSize: CLIENT_PAGINATE_FETCH_ALL, sortField, sortDirection }
        : { pageNumber, pageSize, sortField, sortDirection });
      if (ErrorResponse.isErrorResponse(result)) throw new Error(result.messages.join(', '));

      return result;
    },
    enabled,
    retry: false,
    placeholderData: keepPreviousData,
    staleTime: 10 * 1000,
  });

  const fetchedRows = queryResult?.data;
  const rows = useMemo(() => {
    const all = fetchedRows ?? [];
    return clientPaginate ? all.slice((pageNumber - 1) * pageSize, pageNumber * pageSize) : all;
  }, [fetchedRows, clientPaginate, pageNumber, pageSize]);

  const tableMetaData = useMemo<TableMetaData | undefined>(
    () => (selectMeta && queryResult ? selectMeta(queryResult) : undefined),
    [queryResult, selectMeta]
  );

  const fullCount = queryResult?.data?.length ?? 0;
  const totalCount = clientPaginate ? fullCount : (queryResult?.totalCount ?? 0);
  const totalPages = clientPaginate ? Math.max(1, Math.ceil(fullCount / pageSize)) : (queryResult?.totalPages ?? 0);
  // Note: a cancelled fetch was superseded by a newer one that is already in flight, so it is not a
  // failure and the table waits for that instead. It arrives as the Cancelled string, not an Error.
  const isCancellation = (queryError as unknown) === Cancelled;
  const error = (queryError && !isCancellation && !queryResult) ? (queryError.message ?? i18n.t('table.errorFallback')) : null;
  const isBackgroundFetching = isFetching && !isPending;

  const handleSortChange = useCallback((field: string) => {
    setSortField(prev => {
      if (prev === field) {
        setSortDirection(d => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDirection('asc');
      return field;
    });
  }, []);

  const queryKeyRef = useRef(queryKey); // Note: making it a ref so that mutateCachedResult always has the latest value
  queryKeyRef.current = queryKey;

  const mutateCachedResult = useCallback(
    (mutator: (old: FetchResultType) => FetchResultType) => {
      queryClient.setQueryData<FetchResultType>(queryKeyRef.current, old => old ? mutator(old) : old);
    },
    [queryClient]
  );

  const updateRowSource = useCallback(
    (idSelector: (r: T) => boolean, newRow: T, metaPatch?: Partial<FetchResultType>) =>
      mutateCachedResult(old => ({
        ...old,
        data: old.data.map(r => idSelector(r) ? newRow : r),
        ...metaPatch,
      }) as FetchResultType),
    [mutateCachedResult]
  );

  const appendRowSource = useCallback(
    (newItem: T, metaPatch?: Partial<FetchResultType>) =>
      queryClient.setQueryData<FetchResultType>(queryKeyRef.current, old => {
        if (old) return {
          ...old,
          data: [newItem, ...old.data],
          totalCount: old.totalCount + 1,
          ...metaPatch,
        } as FetchResultType;
        return {
          data: [newItem],
          totalCount: 1,
          totalPages: 1,
          pageNumber,
          pageSize,
          sortField,
          sortDirection,
          ...metaPatch,
        } as unknown as FetchResultType;
      }),
    [queryClient, pageNumber, pageSize, sortField, sortDirection]
  );

  const removeRowSource = useCallback(
    (idSelector: (r: T) => boolean, metaPatch?: Partial<FetchResultType>) =>
      mutateCachedResult(old => ({
        ...old,
        data: old.data.filter(r => !idSelector(r)),
        totalCount: Math.max(old.totalCount - 1, 0),
        ...metaPatch,
      }) as FetchResultType),
    [mutateCachedResult]
  );

  const callerQueryKeyRef = useRef(callerQueryKey);
  callerQueryKeyRef.current = callerQueryKey;

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['dataTable', ...callerQueryKeyRef.current] }),
    [queryClient]
  );

  const refresh = useCallback(() => refetch(), [refetch]);

  return {
    queryKey,
    rows,
    fetchedRows,
    tableMetaData,
    isLoading,
    isBackgroundFetching,
    error,
    pageNumber,
    pageSize,
    totalCount,
    totalPages,
    sortField,
    sortDirection,
    setPage,
    handleSortChange,
    updateRowSource,
    appendRowSource,
    removeRowSource,
    mutateCachedResult,
    invalidate,
    refresh,
  };
}